import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { Button, Flex, LogoutOutlined, Text } from '@/uicomponents';
import { useAuthStore } from '@/stores/auth-store';
import { useLogoutMutation } from '@/app/auth/hooks';
import { stopRefreshTimer } from '@/services/token-refresh';
import { queryClient } from '@/lib/query';
import { Show } from '@/components/shared';
import styles from './impersonation-banner.module.css';

/**
 * Rendered at the top of AppLayout whenever the current session is an
 * impersonation (signal: `user.impersonation.active`).
 *
 * Exit Session calls the shared logout endpoint — backend detects the
 * `impersonation_session` cookie and handles the DB-side cleanup. On success
 * we try `window.close()` (tab was opened by JetAdmin, closing is natural
 * UX) and fall back to pushing `/login` if the browser blocks it.
 */
export function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.user?.impersonation ?? null);
  const targetEmail = useAuthStore((s) => s.user?.email ?? '');
  const tenantName = useAuthStore((s) => s.user?.tenantName ?? '');
  const clearAuth = useAuthStore((s) => s.clear);
  const navigate = useNavigate();
  const { mutate: logoutMutate, isPending } = useLogoutMutation();

  const handleExit = useCallback(() => {
    stopRefreshTimer();
    logoutMutate(undefined, {
      onSettled: () => {
        queryClient.clear();
        clearAuth();
        const closed = window.close();
        // Some browsers block programmatic close — fall back to login.
        if (closed === undefined) {
          navigate('/login');
        }
      },
    });
  }, [clearAuth, logoutMutate, navigate]);

  return (
    <Show when={impersonation?.active === true}>
      <Flex align='center' justify='space-between' gap={16} className={styles.banner}>
        <Text weight='semibold'>
          You are viewing as {targetEmail}
          <Show when={!!tenantName}> ({tenantName})</Show>
        </Text>
        <Button
          type='primary'
          danger
          size='small'
          icon={<LogoutOutlined />}
          loading={isPending}
          onClick={handleExit}
        >
          Exit Session
        </Button>
      </Flex>
    </Show>
  );
}
